<template>
    <div class="rounded-lgr border border-valence-grey-200 bg-white p-3" :class="question.enabled ? '' : 'opacity-60'">
        <div class="flex items-start gap-2">
            <button type="button" class="question-handle cursor-grab text-valence-grey-600 hover:text-valence-grey-850 pt-2" aria-label="Drag to reorder question">
                <i class="bi bi-grip-vertical" aria-hidden="true"></i>
            </button>

            <div class="grow flex flex-col gap-2">
                <textarea
                    :value="question.text"
                    rows="2"
                    placeholder="What should the rater be asked?"
                    :aria-label="`Question ${index + 1} text`"
                    class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                    @focus="$emit('focus')"
                    @input="$emit('update', { text: ($event.target as HTMLTextAreaElement).value })"></textarea>

                <div class="flex flex-wrap items-center gap-2">
                    <select
                        :value="question.question_type"
                        :aria-label="`Question ${index + 1} type`"
                        class="px-2 py-1.5 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 details"
                        @change="$emit('update', { question_type: ($event.target as HTMLSelectElement).value as QuestionType })">
                        <option v-for="option in questionTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>

                    <select
                        v-if="question.question_type === 'Score'"
                        :value="question.scale ?? ''"
                        :aria-label="`Question ${index + 1} scale`"
                        class="px-2 py-1.5 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 details"
                        @change="$emit('update', { scale: ($event.target as HTMLSelectElement).value || null })">
                        <option value="">Template default ({{ defaultScaleLabel }})</option>
                        <option v-for="option in scales" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>

                    <input
                        :value="question.title"
                        type="text"
                        maxlength="255"
                        placeholder="Eyebrow label (optional)"
                        :aria-label="`Question ${index + 1} label`"
                        class="px-2 py-1.5 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 details w-52"
                        @focus="$emit('focus')"
                        @input="$emit('update', { title: ($event.target as HTMLInputElement).value })" />

                    <label :for="`${uid}-optional`" class="flex items-center gap-1.5 details text-valence-grey-750">
                        <input
                            :id="`${uid}-optional`"
                            :checked="question.is_optional"
                            type="checkbox"
                            @change="$emit('update', { is_optional: ($event.target as HTMLInputElement).checked })" />
                        Optional
                    </label>

                    <label :for="`${uid}-enabled`" class="flex items-center gap-1.5 details text-valence-grey-750">
                        <input
                            :id="`${uid}-enabled`"
                            :checked="question.enabled"
                            type="checkbox"
                            @change="$emit('update', { enabled: ($event.target as HTMLInputElement).checked })" />
                        Enabled
                    </label>

                    <div class="ml-auto flex items-center gap-1">
                        <button type="button" class="details text-valence-grey-750 hover:text-valence-grey-850 px-1.5 py-1" @click="$emit('duplicate')">
                            <i class="bi bi-files" aria-hidden="true"></i> Duplicate
                        </button>
                        <BaseTooltip v-if="locked">
                            <template #trigger>
                                <span class="details text-valence-grey-600 px-1.5 py-1"><i class="bi bi-lock" aria-hidden="true"></i> Answered</span>
                            </template>
                            <template #content>This question already has responses, so it can only be disabled — not deleted.</template>
                        </BaseTooltip>
                        <button v-else type="button" class="details text-red-600 hover:text-red-700 px-1.5 py-1" @click="$emit('remove')">
                            <i class="bi bi-trash" aria-hidden="true"></i> Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import type { BuilderChoice, BuilderQuestion, BuilderScaleOption, QuestionType } from "~vue/SurveyBuilder/types";
import { useId } from "vue";

defineProps<{
    question: BuilderQuestion;
    index: number;
    questionTypes: BuilderChoice[];
    scales: BuilderScaleOption[];
    defaultScaleLabel: string;
    /** True when responses exist, which makes the question undeletable. */
    locked: boolean;
}>();

defineEmits<{ focus: []; duplicate: []; remove: []; update: [changes: Partial<BuilderQuestion>] }>();

const uid = useId();
</script>
